import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import type { AppState } from '../store';

export interface ColumnFilter {
  id: string;
  value: unknown;
}

export interface ColumnSort {
  desc: boolean;
  id: string;
}

export type ColumnFiltersState = ColumnFilter[];

export enum ColorMapBy {
  LG_ISO_TYPE = 'igIsotype',
  LEIDEN_CLUSTER = 'leidenCluster',
  CLONOTYPE_ID = 'clonotypeId',
  SHM = 'shm',
  LIABILITY = 'liability',
}

/** A node group as sent to the antibody table API — integer PKs only, no barcode strings */
export interface SelectedNode {
  clonotypeId: number;
  nodeId: number;
  cellIds: number[];
}

export interface ClonotypeState {
  [experimentId: string]: {
    selectedClonotypes: number[];
    selectedCells: string[];
    /** Node-grouped selection for the antibody table API request */
    selectedNodes: SelectedNode[];
    filters: ColumnFilter[];
    sorting: ColumnSort;
    colorMapBy: ColorMapBy | null;
    gridSize: number;
    /** Whether the inline network-graph grid is expanded on the Antibody selection page. */
    isGridExpanded: boolean;
    /** Mosaic vertical split percentage (top tile = grid). 0–100. */
    gridSplitPercentage: number;
  };
}

const initialState: ClonotypeState = {};

const defaultFilters: ColumnFiltersState = [];

export const defaultSorting: ColumnSort = {
  id: 'clonotypeSize',
  desc: true,
};

export const defaultColorMapBy = ColorMapBy.SHM;
export const defaultGridSize = 16;
export const defaultGridExpanded = true;
export const defaultGridSplitPercentage = 40;

function createDefaultExperimentState(): ClonotypeState[string] {
  return {
    selectedClonotypes: [],
    selectedCells: [],
    selectedNodes: [],
    filters: defaultFilters,
    sorting: defaultSorting,
    colorMapBy: defaultColorMapBy,
    gridSize: defaultGridSize,
    isGridExpanded: defaultGridExpanded,
    gridSplitPercentage: defaultGridSplitPercentage,
  };
}

const clonotypeSlice = createSlice({
  name: 'clonotypes',
  initialState,
  reducers: {
    setSelectedClonotypes: (state, action: PayloadAction<{ experimentId: string; clonotypes: number[] }>) => {
      const { experimentId, clonotypes } = action.payload;
      if (!state[experimentId]) {
        state[experimentId] = createDefaultExperimentState();
      }
      state[experimentId].selectedClonotypes = clonotypes;
    },
    addSelectedClonotype: (state, action: PayloadAction<{ experimentId: string; clonotypeId: number }>) => {
      const { experimentId, clonotypeId } = action.payload;
      if (!state[experimentId]) {
        state[experimentId] = createDefaultExperimentState();
      }
      state[experimentId].selectedClonotypes.push(clonotypeId);
    },
    removeSelectedClonotype: (state, action: PayloadAction<{ experimentId: string; clonotypeId: number }>) => {
      const { experimentId, clonotypeId } = action.payload;
      if (!state[experimentId]) {
        state[experimentId] = createDefaultExperimentState();
      }
      state[experimentId].selectedClonotypes = state[experimentId].selectedClonotypes.filter((id) => id !== clonotypeId);
    },
    setClonotypeFilters: (state, action: PayloadAction<{ experimentId: string; filters: ColumnFilter[] }>) => {
      const { experimentId, filters } = action.payload;
      if (!state[experimentId]) {
        state[experimentId] = createDefaultExperimentState();
      }
      state[experimentId].filters = filters;
    },
    setClonotypesSorting: (state, action: PayloadAction<{ experimentId: string; sorting: ColumnSort }>) => {
      const { experimentId, sorting } = action.payload;
      if (!state[experimentId]) {
        state[experimentId] = createDefaultExperimentState();
      }
      state[experimentId].sorting = sorting;
    },
    setColorMapBy: (state, action: PayloadAction<{ experimentId: string; colorMapBy: ColorMapBy | null }>) => {
      const { experimentId, colorMapBy } = action.payload;
      if (!state[experimentId]) {
        state[experimentId] = createDefaultExperimentState();
      }
      state[experimentId].colorMapBy = colorMapBy;
    },
    setGridSize: (state, action: PayloadAction<{ experimentId: string; gridSize: number }>) => {
      const { experimentId, gridSize } = action.payload;
      if (!state[experimentId]) {
        state[experimentId] = createDefaultExperimentState();
      }
      state[experimentId].gridSize = gridSize;
    },
    setSelectedCells: (state, action: PayloadAction<{ experimentId: string; cells: string[] }>) => {
      const { experimentId, cells } = action.payload;
      if (!state[experimentId]) {
        state[experimentId] = createDefaultExperimentState();
      }
      state[experimentId].selectedCells = cells;
    },
    setSelectedNodes: (state, action: PayloadAction<{ experimentId: string; nodes: SelectedNode[] }>) => {
      const { experimentId, nodes } = action.payload;
      if (!state[experimentId]) {
        state[experimentId] = createDefaultExperimentState();
      }
      state[experimentId].selectedNodes = nodes;
    },
    /**
     * Atomic replacement of clonotype/cell/node selection in a single reducer pass.
     * Use for bulk operations like "Select all" so downstream subscribers
     * (AB table fetch, network-map re-render) only re-render once
     */
    setClonotypeSelection: (state, action: PayloadAction<{ experimentId: string; clonotypes: number[]; cells: string[]; nodes: SelectedNode[] }>) => {
      const { experimentId, clonotypes, cells, nodes } = action.payload;
      if (!state[experimentId]) {
        state[experimentId] = createDefaultExperimentState();
      }
      state[experimentId].selectedClonotypes = clonotypes;
      state[experimentId].selectedCells = cells;
      state[experimentId].selectedNodes = nodes;
    },
    setIsGridExpanded: (state, action: PayloadAction<{ experimentId: string; isGridExpanded: boolean }>) => {
      const { experimentId, isGridExpanded } = action.payload;
      if (!state[experimentId]) {
        state[experimentId] = createDefaultExperimentState();
      }
      state[experimentId].isGridExpanded = isGridExpanded;
    },
    setGridSplitPercentage: (state, action: PayloadAction<{ experimentId: string; splitPercentage: number }>) => {
      const { experimentId, splitPercentage } = action.payload;
      if (!state[experimentId]) {
        state[experimentId] = createDefaultExperimentState();
      }
      state[experimentId].gridSplitPercentage = splitPercentage;
    },
    resetClonotypes: () => initialState,
  },
});
export const selectColorMapBy = (state: AppState, experimentId: string): ColorMapBy | null => {
  const stored = state.clonotypes[experimentId]?.colorMapBy;
  return stored === undefined ? defaultColorMapBy : stored;
};

export const {
  setSelectedClonotypes,
  addSelectedClonotype,
  removeSelectedClonotype,
  setClonotypeFilters,
  setClonotypesSorting,
  setColorMapBy,
  setGridSize,
  setSelectedCells,
  setSelectedNodes,
  setClonotypeSelection,
  setIsGridExpanded,
  setGridSplitPercentage,
  resetClonotypes,
} = clonotypeSlice.actions;
export default clonotypeSlice.reducer;
